import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle, AlertTriangle, Clock, Award, Shield, Sparkles, Heart, Users } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import { clinicInfo } from '../data/servicesData';

const AlignerCenterForWoman = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const treatmentTimeline = [
    { stage: 'Consultation & Scan', timeframe: 'Day 1', description: '3D scan + smile analysis' },
    { stage: 'Digital Planning', timeframe: '1–2 weeks', description: 'AI + orthodontist-guided plan' },
    { stage: 'Active Aligner Phase', timeframe: '4–9 months', description: 'New aligners every 1–2 weeks' },
    { stage: 'Refinements (if needed)', timeframe: '1–2 months', description: 'Precision finishing' },
    { stage: 'Retainers', timeframe: 'Long-term', description: 'Smile stability & relapse prevention' }
  ];

  const smileProblems = [
    'Mild to moderate crowding',
    'Gaps (spacing issues)',
    'Overbite / underbite (case-dependent)',
    'Crossbite',
    'Relapse after childhood braces'
  ];

  const faqs = [
    {
      question: 'Is aligner treatment safe before marriage or pregnancy?',
      answer: "Yes. It's non-invasive and does not affect fertility or hormones."
    },
    {
      question: 'Will aligners change my face shape?',
      answer: 'Proper planning enhances facial harmony; poor planning can flatten smiles—expertise matters.'
    },
    {
      question: 'Can I drink coffee or tea?',
      answer: 'Yes. Remove aligners, rinse, reinsert.'
    },
    {
      question: 'What if I forget to wear aligners one day?',
      answer: 'Occasional misses are okay; repeated lapses delay results.'
    }
  ];

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-content">
          <div className="blog-breadcrumb">
            <Link to="/">Home</Link> / <Link to="/clear-aligners-east-tambaram">Aligners</Link> / <span>No.1 Aligner Center for Women</span>
          </div>
          <h1 className="blog-title">
            Why Young Women Choose Dr. Thamarai's Dental Care for Aligners
          </h1>
          <p className="blog-tagline">
            Being called "No.1" isn't about ads—it's about clinical systems + patient outcomes.
          </p>
          <div className="blog-cta-buttons">
            <a href={`tel:${clinicInfo.phone}`} className="cta-button primary">
              <Phone size={20} />
              Call {clinicInfo.phone}
            </a>
            <a href="https://wa.me/917502297680" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
              Book Consultation
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="blog-content">
        <div className="blog-container">
          
          {/* Section 1 */}
          <section className="blog-section">
            <div className="section-number">1</div>
            <h2>Women-Centric Aligner Planning</h2>
            <p>At Dr. Thamarai's Dental Care, aligner treatment is planned with:</p>
            <ul className="feature-list">
              <li><CheckCircle size={20} /> Facial aesthetics & smile arc design</li>
              <li><CheckCircle size={20} /> Lip support and profile harmony</li>
              <li><CheckCircle size={20} /> Jaw comfort and bite balance</li>
              <li><CheckCircle size={20} /> Lifestyle needs of young women (work, studies, social visibility)</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="blog-section">
            <div className="section-number">2</div>
            <h2>Advanced Clear Aligner Systems (Not Generic Trays)</h2>
            <p>
              The clinic uses globally trusted aligner technologies such as <strong>Invisalign</strong> and equivalent high-precision systems—never unverified plastics.
            </p>
          </section>

          {/* Section 3 */}
          <section className="blog-section">
            <div className="section-number">3</div>
            <h2>Fully Digital, Predictable Treatment</h2>
            <ul className="feature-list">
              <li><CheckCircle size={20} /> 3D intra-oral scans (no messy impressions)</li>
              <li><CheckCircle size={20} /> AI-based tooth movement simulation</li>
              <li><CheckCircle size={20} /> Clear preview of your future smile before starting</li>
              <li><CheckCircle size={20} /> Reduced risk of mid-treatment surprises</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="blog-section">
            <div className="section-number">4</div>
            <h2>Safety, Hygiene & Ethical Care</h2>
            <ul className="feature-list">
              <li><Shield size={20} /> Female-friendly, private clinical environment</li>
              <li><Shield size={20} /> Strict sterilization & infection control</li>
              <li><Shield size={20} /> Transparent consent and documented treatment plans</li>
              <li><Shield size={20} /> No shortcuts, no unsafe online-only aligners</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="blog-section">
            <div className="section-number">5</div>
            <h2>Local Accountability in Tambaram</h2>
            <ul className="feature-list">
              <li><CheckCircle size={20} /> In-clinic orthodontic supervision</li>
              <li><CheckCircle size={20} /> Easy follow-ups without long travel</li>
              <li><CheckCircle size={20} /> Immediate support if aligners feel uncomfortable</li>
            </ul>
          </section>

          {/* Why Aligners Section */}
          <section className="blog-section highlight-section">
            <h2><Sparkles size={28} /> Why Clear Aligners Are Ideal for Women Under 30</h2>
            
            <div className="benefits-grid">
              <div className="benefit-card">
                <h3><Heart size={24} /> Lifestyle & Confidence</h3>
                <ul>
                  <li>Nearly invisible in photos, meetings & reels</li>
                  <li>Removable for weddings, festivals & special occasions</li>
                  <li>No food restrictions</li>
                </ul>
              </div>

              <div className="benefit-card">
                <h3><Shield size={24} /> Comfort & Oral Health</h3>
                <ul>
                  <li>Fewer ulcers than metal braces</li>
                  <li>Easier brushing & flossing</li>
                  <li>Gentle, controlled tooth movement</li>
                </ul>
              </div>

              <div className="benefit-card">
                <h3><Award size={24} /> Career & Social Advantage</h3>
                <ul>
                  <li>No "braces look" during interviews or office hours</li>
                  <li>Confidence boost often visible within weeks</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Problems Treated */}
          <section className="blog-section">
            <h2>Smile Problems Treated with Aligners at Dr. Thamarai's Dental Care</h2>
            <p>Clear aligners are highly effective for:</p>
            <div className="problems-grid">
              {smileProblems.map((problem, index) => (
                <div key={index} className="problem-item">
                  <CheckCircle size={20} />
                  <span>{problem}</span>
                </div>
              ))}
            </div>
            <div className="clinical-insight">
              <strong>Clinical Insight:</strong> Treating alignment issues before 30 helps prevent gum problems, jaw stress, and premature tooth wear later in life.
            </div>
          </section>

          {/* Treatment Timeline */}
          <section className="blog-section">
            <h2><Clock size={28} /> Clear Aligner Treatment Timeline</h2>
            <div className="timeline-table">
              <div className="timeline-header">
                <span>Stage</span>
                <span>Timeframe</span>
                <span>What Happens</span>
              </div>
              {treatmentTimeline.map((item, index) => (
                <div key={index} className="timeline-row">
                  <span className="stage-name">{item.stage}</span>
                  <span className="stage-time">{item.timeframe}</span>
                  <span className="stage-desc">{item.description}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Cost Section */}
          <section className="blog-section cost-section">
            <h2>Cost of Clear Aligners in Tambaram (Realistic & Transparent)</h2>
            <div className="cost-highlight">
              <span className="cost-label">Expected Investment</span>
              <span className="cost-value">₹65,000 – ₹1,50,000</span>
            </div>
            <p>Final cost depends on:</p>
            <ul className="cost-factors">
              <li>Complexity of tooth movement</li>
              <li>Aligner system used</li>
              <li>Treatment duration</li>
              <li>Refinements & retainers included</li>
            </ul>
            <div className="warning-box">
              <AlertTriangle size={24} />
              <p><strong>Important Warning:</strong> Extremely low-cost aligner ads often skip diagnostics, supervision, or refinement trays—leading to relapse or bite issues.</p>
            </div>
          </section>

          {/* Myths Section */}
          <section className="blog-section myths-section">
            <h2>Common Myths Young Women Believe (Corrected by Experts)</h2>
            <div className="myths-grid">
              <div className="myth-card">
                <div className="myth-label">Myth</div>
                <p className="myth-text">Aligners are only cosmetic</p>
                <div className="truth-label">Truth</div>
                <p className="truth-text">Properly planned aligners improve bite, gum health, and jaw balance.</p>
              </div>
              <div className="myth-card">
                <div className="myth-label">Myth</div>
                <p className="myth-text">No pain means no results</p>
                <div className="truth-label">Truth</div>
                <p className="truth-text">Gentle force is safer and more biologically effective.</p>
              </div>
              <div className="myth-card">
                <div className="myth-label">Myth</div>
                <p className="myth-text">Online aligners are enough</p>
                <div className="truth-label">Truth</div>
                <p className="truth-text">Unsupervised treatment risks root damage and permanent misalignment.</p>
              </div>
            </div>
          </section>

          {/* Safety Section */}
          <section className="blog-section safety-section">
            <h2><Shield size={28} /> Safety, Ethics & Regulations in India</h2>
            <p>At Dr. Thamarai's Dental Care:</p>
            <ul className="feature-list">
              <li><CheckCircle size={20} /> Aligner treatment is supervised by licensed dental professionals</li>
              <li><CheckCircle size={20} /> X-rays & scans are mandatory for safe planning</li>
              <li><CheckCircle size={20} /> Retainers are compulsory to maintain results</li>
              <li><CheckCircle size={20} /> Patient data & treatment records are fully documented</li>
            </ul>
            <p className="emphasis-text">
              This is <strong>medical orthodontics</strong>, not cosmetic experimentation.
            </p>
          </section>

          {/* FAQs Section */}
          <section className="blog-section faq-section">
            <h2><Users size={28} /> FAQs Women Under 30 Actually Search</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item-blog">
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="blog-cta-section">
            <h2>Ready to Transform Your Smile?</h2>
            <p>Book a consultation with Dr. Thamarai's Dental Care today and take the first step towards your dream smile.</p>
            <div className="blog-cta-buttons">
              <a href={`tel:${clinicInfo.phone}`} className="cta-button primary">
                <Phone size={20} />
                Call {clinicInfo.phone}
              </a>
              <a href="https://wa.me/917502297680" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
                WhatsApp Us
                <ArrowRight size={20} />
              </a>
            </div>
          </section>

        </div>
      </article>

      <BookingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        pageTitle="Aligner Center for Women"
      />
    </div>
  );
};

export default AlignerCenterForWoman;
